import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/appStore';
import { apiClient } from '../../services/api';
import { chatService } from '../../services/chat.service';
import styles from './booking.module.css';

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

interface BookingData {
  checkInDate: string;
  checkOutDate: string;
  boatLength: number;
  boatWidth: number;
  boatDraft: number;
  boatModel: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerNotes: string;
}

export const BookingForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const viewMode = useAppStore((state) => state.viewMode);
  const setViewMode = useAppStore((state) => state.setViewMode);

  const [formData, setFormData] = useState<BookingData>({
    checkInDate: '',
    checkOutDate: '',
    boatLength: 0,
    boatWidth: 0,
    boatDraft: 0,
    boatModel: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerNotes: '',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [searchingAvailability, setSearchingAvailability] = useState(false);
  const [_availableMoorings, setAvailableMoorings] = useState<any[]>([]);
  const [_searchError, setSearchError] = useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const isAvatarSpeaking = useAppStore((state) => state.isAvatarSpeaking);
  const audioElement = useAppStore((state) => state.audioElement);
  const addMessage = useAppStore((state) => state.addMessage);
  const clearMessages = useAppStore((state) => state.clearMessages);
  const setAvatarSpeaking = useAppStore((state) => state.setAvatarSpeaking);
  const setCurrentVisemes = useAppStore((state) => state.setCurrentVisemes);
  const setAudioElement = useAppStore((state) => state.setAudioElement);
  const lastSpokenFormStep = useAppStore((state) => state.lastSpokenFormStep);
  const setLastSpokenFormStep = useAppStore((state) => state.setLastSpokenFormStep);
  const instructionsSpoken = useAppStore((state) => state.instructionsSpoken);
  const setInstructionsSpoken = useAppStore((state) => state.setInstructionsSpoken);
  const isChangingLanguage = useAppStore((state) => state.isChangingLanguage);

  const steps = [
    {
      label: t('booking.form.boatDetails'),
      fields: ['boatModel', 'boatLength', 'boatWidth', 'boatDraft'],
      message: t('booking.form.step1Message')
    },
    {
      label: t('booking.form.reservationDates'),
      fields: ['checkInDate', 'checkOutDate'],
      message: t('booking.form.step2Message'),
      searchAvailability: true
    },
    {
      label: t('booking.form.customerInfo'),
      fields: ['customerName', 'customerEmail', 'customerPhone', 'customerNotes'],
      message: t('booking.form.step3Message')
    },
  ];

  const stopCurrentAudio = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioElement(null);
      setAvatarSpeaking(false);
      setCurrentVisemes([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('Length') || name.includes('Width') || name.includes('Draft') ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Create audio placeholder immediately in user interaction context (for mobile compatibility)
    const audioPlaceholder = new Audio();
    audioPlaceholder.src = 'data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAABhTEFNRTMuMTAwA8MAAAAAAAAAABQgJAUHQQAB9AAAAnGMHkkIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==';
    console.log('[BookingForm] 📱 Created audio placeholder for form submit');

    try {
      setAvatarSpeaking(true);
      const message = t('booking.form.processingMessage');

      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: message,
        timestamp: Date.now(),
      });

      // Synthesize processing message
      const ttsResponse = await apiClient.synthesize({ text: message, language: i18n.language });

      if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
        setCurrentVisemes(ttsResponse.visemes);
      }

      if (ttsResponse.audio_base64) {
        // Reuse placeholder instead of creating new Audio
        audioPlaceholder.src = `data:audio/mpeg;base64,${ttsResponse.audio_base64}`;
        setAudioElement(audioPlaceholder);
        audioPlaceholder.onended = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };
        audioPlaceholder.onerror = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };
        audioPlaceholder.play().catch(() => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        });
      }

      // Wait for processing audio to finish before submitting
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Submit booking with all boat details
      const response = await apiClient.submitBooking({
        mooring_name: 'Auto-Booked',
        check_in_date: formData.checkInDate,
        check_out_date: formData.checkOutDate,
        contact_name: formData.customerName,
        contact_email: formData.customerEmail,
        contact_phone: formData.customerPhone,
        boat_model: formData.boatModel,
        boat_length: formData.boatLength,
        boat_width: formData.boatWidth,
        boat_draft: formData.boatDraft,
        notes: formData.customerNotes,
      });

      console.log('Booking submitted:', response);

      // Stop processing audio if still playing
      stopCurrentAudio();

      // Show success message with detailed confirmation (same as chat mode)
      const successMsg = `
✅ ${t('booking.confirmation.title')}

${t('booking.confirmation.boat')}: ${formData.boatModel}
${t('booking.confirmation.checkIn')}: ${formatDate(formData.checkInDate)}
${t('booking.confirmation.checkOut')}: ${formatDate(formData.checkOutDate)}
${t('booking.confirmation.contact')}: ${formData.customerName}

${t('booking.confirmation.emailNotice')}
      `.trim();

      // Set local state to show confirmation message
      setConfirmationMessage(successMsg);

      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: successMsg,
        timestamp: Date.now(),
      });

      // Synthesize confirmation message
      const successTtsResponse = await apiClient.synthesize({ text: successMsg, language: i18n.language });

      if (successTtsResponse.visemes && successTtsResponse.visemes.length > 0) {
        setCurrentVisemes(successTtsResponse.visemes);
      }

      if (successTtsResponse.audio_base64) {
        // Create new audio placeholder for success message
        const successAudio = new Audio();
        successAudio.src = `data:audio/mpeg;base64,${successTtsResponse.audio_base64}`;
        setAudioElement(successAudio);
        setAvatarSpeaking(true);

        successAudio.onended = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
          // Wait a bit before closing to let user read the message
          setTimeout(() => {
            setShowForm(false);
            clearMessages();
            chatService.clearHistory();
            setViewMode('initial');
          }, 3000);
        };

        successAudio.onerror = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
          setTimeout(() => {
            setShowForm(false);
            clearMessages();
            chatService.clearHistory();
            setViewMode('initial');
          }, 3000);
        };

        successAudio.play().catch(() => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
          setTimeout(() => {
            setShowForm(false);
            clearMessages();
            chatService.clearHistory();
            setViewMode('initial');
          }, 3000);
        });
      } else {
        setAvatarSpeaking(false);
        setTimeout(() => {
          setShowForm(false);
          clearMessages();
          chatService.clearHistory();
          setViewMode('initial');
        }, 3000);
      }
    } catch (error) {
      console.error('Booking error:', error);

      // Stop any audio
      stopCurrentAudio();

      const errorMessage = t('booking.form.errorMessage');
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: errorMessage,
        timestamp: Date.now(),
      });

      // Synthesize error message
      const errorTtsResponse = await apiClient.synthesize({ text: errorMessage, language: i18n.language });

      if (errorTtsResponse.visemes && errorTtsResponse.visemes.length > 0) {
        setCurrentVisemes(errorTtsResponse.visemes);
      }

      if (errorTtsResponse.audio_base64) {
        const audio = new Audio(`data:audio/mpeg;base64,${errorTtsResponse.audio_base64}`);
        setAudioElement(audio);
        setAvatarSpeaking(true);

        audio.onended = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };

        audio.onerror = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };

        audio.play().catch(() => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        });
      } else {
        setAvatarSpeaking(false);
      }
    }
  };

  const isStepComplete = (stepIndex: number) => {
    const step = steps[stepIndex];
    const allFieldsFilled = step.fields.every((field) => {
      const value = formData[field as keyof BookingData];
      return value !== '' && value !== 0;
    });

    // Se è lo step delle date (step 1), valida anche che check-in < check-out
    if (stepIndex === 1 && allFieldsFilled) {
      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      return checkIn < checkOut;
    }

    return allFieldsFilled;
  };

  const canProceedToNext = isStepComplete(currentStep);

  // Search for available moorings
  const searchAvailableMoorings = async () => {
    const checkInDate = formData.checkInDate;
    const checkOutDate = formData.checkOutDate;
    const boatLength = formData.boatLength;
    const boatWidth = formData.boatWidth;
    const boatDraft = formData.boatDraft;

    // Validate dates
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);

    if (checkIn >= checkOut) {
      const errorMsg = t('booking.form.invalidDates');
      setSearchError(errorMsg);

      // Make avatar speak the error
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: errorMsg,
        timestamp: Date.now(),
      });

      setAvatarSpeaking(true);

      try {
        const ttsResponse = await apiClient.synthesize({ text: errorMsg, language: i18n.language });

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          setCurrentVisemes(ttsResponse.visemes);
        }

        if (ttsResponse.audio_base64) {
          const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
          setAudioElement(audio);

          audio.onended = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audio.onerror = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audio.play().catch(() => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          });
        }
      } catch (error) {
        console.error('TTS error:', error);
        setAvatarSpeaking(false);
      }

      return;
    }

    try {
      setSearchingAvailability(true);
      setSearchError(null);

      // Call the API endpoint that checks availability
      const response = await apiClient.searchMoorings({
        checkInDate,
        checkOutDate,
        boatLength,
        boatWidth,
        boatDraft,
      });

      // Check if we have available moorings
      if (response.available && response.available.length > 0) {
        setAvailableMoorings(response.available);

        // Make avatar speak about results
        const message = t('booking.form.availabilityFound');

        addMessage({
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: message,
          timestamp: Date.now(),
        });

        // Synthesize audio
        const ttsResponse = await apiClient.synthesize({ text: message, language: i18n.language });

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          setCurrentVisemes(ttsResponse.visemes);
        }

        if (ttsResponse.audio_base64) {
          const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
          setAudioElement(audio);
          setAvatarSpeaking(true);

          audio.onended = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
            // Advance to next step AFTER audio finishes
            setTimeout(() => {
              setCurrentStep(currentStep + 1);
            }, 500);
          };

          audio.onerror = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
            // Advance even if audio fails
            setTimeout(() => {
              setCurrentStep(currentStep + 1);
            }, 500);
          };

          audio.play().catch((error) => {
            console.error('Audio playback error:', error);
            setAvatarSpeaking(false);
            // Advance even if playback fails
            setTimeout(() => {
              setCurrentStep(currentStep + 1);
            }, 500);
          });
        } else {
          // No audio, advance after short delay
          setTimeout(() => {
            setCurrentStep(currentStep + 1);
          }, 500);
        }
      } else {
        // No availability found
        const errorMsg = t('booking.form.noAvailability');
        setSearchError(errorMsg);

        // Make avatar speak the error
        addMessage({
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: errorMsg,
          timestamp: Date.now(),
        });

        setAvatarSpeaking(true);

        const ttsResponse = await apiClient.synthesize({ text: errorMsg, language: i18n.language });

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          setCurrentVisemes(ttsResponse.visemes);
        }

        if (ttsResponse.audio_base64) {
          const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
          setAudioElement(audio);

          audio.onended = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audio.onerror = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audio.play().catch((error) => {
            console.error('Audio playback error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = t('booking.form.searchError');
      setSearchError(errorMsg);

      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: errorMsg,
        timestamp: Date.now(),
      });

      setAvatarSpeaking(true);

      try {
        const ttsResponse = await apiClient.synthesize({ text: errorMsg, language: i18n.language });

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          setCurrentVisemes(ttsResponse.visemes);
        }

        if (ttsResponse.audio_base64) {
          const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
          setAudioElement(audio);

          audio.onended = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audio.play().catch(() => {});
        }
      } catch (ttsError) {
        console.error('TTS error:', ttsError);
        setAvatarSpeaking(false);
      }
    } finally {
      setSearchingAvailability(false);
    }
  };

  const speakStepMessage = async (stepIndex: number) => {
    const step = steps[stepIndex] as any;
    if (!step.message) return;

    try {
      setAvatarSpeaking(true);

      // Add message to chat
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: step.message,
        timestamp: Date.now(),
      });

      // Synthesize audio
      const ttsResponse = await apiClient.synthesize({
        text: step.message,
        language: i18n.language,
      });

      if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
        setCurrentVisemes(ttsResponse.visemes);
      }

      if (ttsResponse.audio_base64) {
        const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
        setAudioElement(audio);

        audio.onended = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };

        audio.onerror = () => {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        };

        audio.play().catch((error) => {
          console.error('Audio playback error:', error);
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
        });
      } else {
        setAvatarSpeaking(false);
        setCurrentVisemes([]);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setAvatarSpeaking(false);
      setCurrentVisemes([]);
    }
  };

  // Speak step message when form is first shown or step changes
  useEffect(() => {
    // CRITICAL: Don't speak if language change is in progress
    if (isChangingLanguage) {
      console.log('[BookingForm] Skipping step message - language change in progress');
      return;
    }

    if (showForm && viewMode === 'form' && currentStep !== lastSpokenFormStep && !isAvatarSpeaking) {
      // Stop any greeting audio that might be playing
      stopCurrentAudio();
      setLastSpokenFormStep(currentStep);
      speakStepMessage(currentStep);
    }
  }, [showForm, currentStep, viewMode, isChangingLanguage]);

  // Reset form when switching away from form mode
  useEffect(() => {
    if (viewMode !== 'form') {
      // Reset form state when leaving form mode
      setShowForm(false);
      setCurrentStep(0);
      setConfirmationMessage(null);
      setFormData({
        checkInDate: '',
        checkOutDate: '',
        boatLength: 0,
        boatWidth: 0,
        boatDraft: 0,
        boatModel: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerNotes: '',
      });
      setAvailableMoorings([]);
      setSearchError(null);
    }
  }, [viewMode]);

  // Cleanup when component unmounts (e.g., when navigating away)
  useEffect(() => {
    return () => {
      // Stop audio and reset avatar state when leaving the page
      stopCurrentAudio();
      setShowForm(false);
      setCurrentStep(0);
      setConfirmationMessage(null);
      setLastSpokenFormStep(-1);
    };
  }, []);

  // Speak instructions when in initial mode
  useEffect(() => {
    const speakInstructions = async () => {
      // CRITICAL: Don't speak if language change is in progress
      if (isChangingLanguage) {
        console.log('[BookingForm] ❌ BLOCKED - Skipping instructions due to language change');
        return;
      }

      // Only speak if we're in initial mode and haven't spoken yet
      if (viewMode === 'initial' && !instructionsSpoken) {
        console.log('[BookingForm] ✅ Preparing to speak instructions...');
        console.log('[BookingForm] isAvatarSpeaking:', isAvatarSpeaking);
        console.log('[BookingForm] instructionsSpoken:', instructionsSpoken);
        console.log('[BookingForm] isChangingLanguage:', isChangingLanguage);

        // Mark as spoken immediately to prevent double playback
        setInstructionsSpoken(true);

        // Force stop any currently playing audio
        stopCurrentAudio();

        // Wait until avatar is not speaking (important for transition from greeting)
        let waitCount = 0;
        while (useAppStore.getState().isAvatarSpeaking && waitCount < 20) {
          console.log('[BookingForm] Waiting for avatar to stop speaking...', waitCount);
          await new Promise(resolve => setTimeout(resolve, 200));
          waitCount++;
        }

        // Additional wait to ensure audio is fully stopped (important for mobile)
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          // Combine mode1 and mode2 into a single message
          const mode1 = t('booking.instructions.mode1');
          const mode2 = t('booking.instructions.mode2');
          const instructionsText = `${mode1}. ${mode2}`;

          console.log('[BookingForm] Speaking instructions:', instructionsText);
          console.log('[BookingForm] Current language:', i18n.language);

          // Synthesize audio for instructions
          const ttsResponse = await apiClient.synthesize({
            text: instructionsText,
            language: i18n.language,
          });

          console.log('[BookingForm] TTS response received:', {
            hasAudio: !!ttsResponse.audio_base64,
            audioLength: ttsResponse.audio_base64?.length || 0,
            visemeCount: ttsResponse.visemes?.length || 0
          });

          if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
            setCurrentVisemes(ttsResponse.visemes);
          }

          if (ttsResponse.audio_base64) {
            const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
            setAudioElement(audio);
            setAvatarSpeaking(true);

            console.log('[BookingForm] Audio element created, attempting playback...');

            audio.onended = () => {
              console.log('[BookingForm] Instructions audio ended');
              setAvatarSpeaking(false);
              setCurrentVisemes([]);
              setAudioElement(null);
            };

            audio.onerror = (err) => {
              console.error('[BookingForm] Instructions audio error:', err);
              setAvatarSpeaking(false);
              setCurrentVisemes([]);
              setAudioElement(null);
            };

            // Try to play with user gesture fallback for mobile
            try {
              await audio.play();
              console.log('[BookingForm] Instructions audio playing successfully');
            } catch (err) {
              console.error('[BookingForm] Error playing instructions audio:', err);
              setAvatarSpeaking(false);
              setCurrentVisemes([]);
              setAudioElement(null);
            }
          } else {
            console.warn('[BookingForm] No audio_base64 in TTS response');
          }
        } catch (error) {
          console.error('[BookingForm] Error synthesizing instructions:', error);
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
        }
      } else if (viewMode !== 'initial') {
        // Reset the flag when leaving initial mode
        setInstructionsSpoken(false);
      }
    };

    speakInstructions();
  }, [viewMode, t, i18n.language, instructionsSpoken, isChangingLanguage]);

  // Modalità iniziale: mostra entrambi i pulsanti
  if (viewMode === 'initial') {
    return (
      <div className={styles.formTrigger}>
        <div className={styles.instructions}>
          <h3>{t('booking.instructions.title')}</h3>
          <ol>
            <li>{t('booking.instructions.mode1')}</li>
            <li>{t('booking.instructions.mode2')}</li>
          </ol>
        </div>
        <div className={styles.modeButtons}>
          <button
            className={styles.modeBtn}
            onClick={async () => {
              // CRITICAL: Stop instructions audio before switching to chat
              console.log('[BookingForm] Stopping instructions audio before chat mode');
              stopCurrentAudio();

              // Wait a moment for audio to fully stop
              await new Promise(resolve => setTimeout(resolve, 300));

              setViewMode('chat');

              // Send automatic message to start booking process
              const quoteMessage = t('ai.greeting').includes('preventivo')
                ? 'Voglio richiedere un preventivo'
                : 'I want to request a quote';

              try {
                // Send message to AI
                const response = await chatService.sendMessage(quoteMessage, i18n.language);

                // Add user message to chat
                addMessage({
                  id: `msg-${Date.now()}`,
                  role: 'user',
                  content: quoteMessage,
                  timestamp: Date.now(),
                });

                // Add assistant response
                addMessage(response);

                // Synthesize audio
                setAvatarSpeaking(true);
                const ttsResponse = await apiClient.synthesize({
                  text: response.content,
                  language: i18n.language,
                });

                if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
                  setCurrentVisemes(ttsResponse.visemes);
                }

                if (ttsResponse.audio_base64) {
                  const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
                  setAudioElement(audio);

                  audio.onended = () => {
                    setAvatarSpeaking(false);
                    setCurrentVisemes([]);
                    setAudioElement(null);
                  };

                  audio.onerror = () => {
                    setAvatarSpeaking(false);
                    setCurrentVisemes([]);
                    setAudioElement(null);
                  };

                  audio.play().catch(() => {
                    setAvatarSpeaking(false);
                    setCurrentVisemes([]);
                    setAudioElement(null);
                  });
                }
              } catch (error) {
                console.error('Error sending automatic message:', error);
              }
            }}
          >
            💬 {t('booking.useVirtualAssistant')}
          </button>
          <button
            className={styles.modeBtn}
            onClick={async () => {
              // CRITICAL: Stop instructions audio before switching to form
              console.log('[BookingForm] Stopping instructions audio before form mode');
              stopCurrentAudio();

              // Wait a moment for audio to fully stop
              await new Promise(resolve => setTimeout(resolve, 300));

              setViewMode('form');
              setShowForm(true);
            }}
          >
            📋 {t('booking.useForm')}
          </button>
        </div>
      </div>
    );
  }

  // Modalità form: nasconde tutto tranne il form
  if (viewMode === 'form' && !showForm) {
    setShowForm(true);
  }

  // Modalità chat: non mostra il form
  if (viewMode === 'chat') {
    return null;
  }

  // Render del form (solo se viewMode === 'form' e showForm === true)
  if (!showForm) {
    return null;
  }

  return (
    <div className={styles.bookingFormContainer}>
      <div className={styles.formHeader}>
        <h2>{t('booking.form.step')} {currentStep + 1} {t('booking.form.of')} {steps.length}</h2>
        <p className={styles.stepLabel}>{steps[currentStep].label}</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Step 0: Boat Details */}
        {currentStep === 0 && (
          <div className={styles.formStep}>
            <div className={styles.formGroup}>
              <label htmlFor="boatModel">{t('booking.form.boatModel')}</label>
              <input
                type="text"
                id="boatModel"
                name="boatModel"
                value={formData.boatModel}
                onChange={handleInputChange}
                placeholder="e.g., Jeanneau Sun Odyssey"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="boatLength">{t('booking.form.boatLength')}</label>
              <input
                type="number"
                id="boatLength"
                name="boatLength"
                value={formData.boatLength || ''}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                placeholder="e.g., 12.5"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="boatWidth">{t('booking.form.boatWidth')}</label>
              <input
                type="number"
                id="boatWidth"
                name="boatWidth"
                value={formData.boatWidth || ''}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                placeholder="e.g., 4.5"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="boatDraft">{t('booking.form.boatDraft')}</label>
              <input
                type="number"
                id="boatDraft"
                name="boatDraft"
                value={formData.boatDraft || ''}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                placeholder="e.g., 2.0"
                required
              />
            </div>
          </div>
        )}

        {/* Step 1: Reservation Dates */}
        {currentStep === 1 && (
          <div className={styles.formStep}>
            <div className={styles.formGroup}>
              <label htmlFor="checkInDate">{t('booking.form.checkInDate')}</label>
              <input
                type="date"
                id="checkInDate"
                name="checkInDate"
                value={formData.checkInDate}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="checkOutDate">{t('booking.form.checkOutDate')}</label>
              <input
                type="date"
                id="checkOutDate"
                name="checkOutDate"
                value={formData.checkOutDate}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        )}

        {/* Step 2: Customer Info */}
        {currentStep === 2 && (
          <div className={styles.formStep}>
            <div className={styles.formGroup}>
              <label htmlFor="customerName">{t('booking.form.customerName')}</label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="Enter name or company name"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="customerEmail">{t('booking.form.customerEmail')}</label>
              <input
                type="email"
                id="customerEmail"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                placeholder="Enter email address"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="customerPhone">{t('booking.form.customerPhone')}</label>
              <input
                type="tel"
                id="customerPhone"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="customerNotes">{t('booking.form.customerNotes')}</label>
              <textarea
                id="customerNotes"
                name="customerNotes"
                value={formData.customerNotes}
                onChange={handleInputChange}
                placeholder="Enter tax code or additional notes"
                rows={3}
              />
            </div>
          </div>
        )}

        <div className={styles.formFooter}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => {
              if (currentStep > 0) {
                setCurrentStep(currentStep - 1);
              } else {
                setShowForm(false);
              }
            }}
          >
            {currentStep === 0 ? t('booking.form.cancel') : t('booking.form.previous')}
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={!canProceedToNext || searchingAvailability}
              onClick={async () => {
                // If this step requires searching availability, do it
                const step = steps[currentStep] as any;
                if (step.searchAvailability) {
                  await searchAvailableMoorings();
                } else {
                  setCurrentStep(currentStep + 1);
                }
              }}
            >
              {searchingAvailability ? t('booking.form.searching') : currentStep === 0 ? t('booking.form.next') : currentStep === 1 ? t('booking.form.searchContinue') : t('booking.form.next')}
            </button>
          ) : (
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={!canProceedToNext}
            >
              {t('booking.form.submit')}
            </button>
          )}
        </div>
      </form>

      {/* Display confirmation message when booking is successful */}
      {confirmationMessage && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#e8f5e9',
          borderRadius: '8px',
          border: '2px solid #4caf50'
        }}>
          <div style={{
            whiteSpace: 'pre-wrap',
            fontSize: '15px',
            lineHeight: '1.6',
            color: '#1b5e20',
            fontWeight: '500'
          }}>
            {confirmationMessage}
          </div>
        </div>
      )}

      {isAvatarSpeaking && (
        <div className={styles.avatarHint}>
          {t('chat.avatarSpeaking')} 🎧
        </div>
      )}
    </div>
  );
};
